import { useState } from "react";
import { Smartphone, Tablet, Laptop, Gamepad2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import Seo from "@/components/Seo";

const CATEGORIES = [
  { id: "phone", label: "Phone", icon: Smartphone },
  { id: "tablet", label: "Tablet", icon: Tablet },
  { id: "laptop", label: "Laptop or computer", icon: Laptop },
  { id: "console", label: "Games console", icon: Gamepad2 },
];

const OPTIONS = {
  phone: ["iPhone", "Samsung", "Other Android"],
  tablet: ["iPad", "Samsung Tab", "Other tablet"],
  laptop: ["MacBook", "Windows laptop", "Desktop computer"],
  console: ["Xbox", "PlayStation", "Nintendo"],
};

export default function Buy() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    category: "", option: "", details: "", budget: "",
    name: user?.name || "", email: user?.email || "", phone: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/buy-requests", form);
      setDone(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="buy-success">
        <CheckCircle2 className="h-16 w-16 mx-auto mb-6 text-accent" />
        <h1 className="font-display font-black uppercase tracking-tight text-4xl mb-4">Request received</h1>
        <p className="text-neutral-600 mb-2">Your reference is</p>
        <p className="font-display font-black text-3xl tracking-widest border-2 border-black inline-block px-6 py-3 shadow-hard mb-8" data-testid="buy-reference">{done.reference}</p>
        <p className="text-neutral-600 leading-relaxed max-w-md mx-auto">
          We'll check current stock of {form.option} devices and message you with what's available and the price — usually the same working day.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16" data-testid="buy-page">
      <Seo title="Buy a Refurbished Device | Appleman Tech Repair" description="Buy refurbished iPhones, Samsung phones, iPads, MacBooks, laptops and games consoles from Appleman Tech Repair in Hyde, Tameside. Tested, wiped and warranty included." />
      <p className="eyebrow text-accent mb-4">Buy a device</p>
      <h1 className="font-display font-black uppercase tracking-tighter text-5xl mb-4">
        {step === 0 ? "What are you looking for?" : step === 1 ? `Which ${form.category === "laptop" ? "type" : "make"}?` : "Nearly there"}
      </h1>
      <p className="text-neutral-600 mb-10 max-w-lg">
        Refurbished, fully tested and wiped — and every device we sell comes with a warranty. Tell us what you're after and we'll come back with what's in stock and the price.
      </p>

      {step === 0 && (
        <div className="grid grid-cols-2 gap-4" data-testid="buy-step-category">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => { set("category", c.id); setStep(1); }} data-testid={`buy-cat-${c.id}`}
              className="border-2 border-black bg-white p-8 flex flex-col items-center gap-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover">
              <c.icon className="h-10 w-10" strokeWidth={2} /> {c.label}
            </button>
          ))}
        </div>
      )}

      {step === 1 && (
        <div data-testid="buy-step-option">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {OPTIONS[form.category].map((o) => (
              <button key={o} onClick={() => { set("option", o); setStep(2); }} data-testid={`buy-opt-${o.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-2 border-black bg-white p-8 font-display font-bold text-xl uppercase tracking-wider shadow-hard brutal-hover">
                {o}
              </button>
            ))}
          </div>
          <button onClick={() => setStep(0)} data-testid="buy-back-0"
            className="inline-flex items-center gap-2 border-2 border-black bg-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={submit} className="border-2 border-black shadow-hard-lg p-8 space-y-5 bg-white" data-testid="buy-form">
          <p className="font-display font-bold uppercase tracking-wider">
            {form.option} <span className="text-neutral-400">· {CATEGORIES.find((c) => c.id === form.category)?.label}</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2" htmlFor="buy-details">Model / storage / colour (optional)</label>
              <input id="buy-details" value={form.details} onChange={(e) => set("details", e.target.value)} data-testid="buy-details-input"
                placeholder="e.g. iPhone 13, 128GB, black" className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="buy-budget">Budget (optional)</label>
              <input id="buy-budget" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="buy-budget-input"
                placeholder="e.g. up to £250" className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="buy-name">Name</label>
              <input id="buy-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="buy-name-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="buy-phone">Phone</label>
              <input id="buy-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} required placeholder="07…" data-testid="buy-phone-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="eyebrow block mb-2" htmlFor="buy-email">Email</label>
              <input id="buy-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="buy-email-input"
                className="w-full border-2 border-black px-4 py-3 outline-none focus:shadow-hard-sm" />
            </div>
          </div>
          <div className="flex justify-between items-center gap-4 pt-2">
            <button type="button" onClick={() => setStep(1)} data-testid="buy-back-1"
              className="inline-flex items-center gap-2 border-2 border-black bg-white px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button type="submit" disabled={busy} data-testid="buy-submit-btn"
              className="border-2 border-black bg-accent px-8 py-3 font-display font-bold uppercase tracking-wider shadow-hard brutal-hover disabled:opacity-50">
              {busy ? "Sending…" : "Check stock for me"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
